import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ArchiveErrorLogsInput,
  CollectionMeta,
  ErrorLog,
  ErrorLogBulkResult,
  ErrorLogDetail,
  ErrorLogSource,
  ErrorLogStatus,
  PurgeErrorLogsInput,
  UpdateErrorLogStatusInput,
} from '@neuroedge/shared';
import { apiClient, type ApiClientError } from './client';

/** Platform admin: in-house error tracking (`/admin/error-logs`). */

const ERROR_LOGS_KEY = ['admin', 'error-logs'] as const;

export interface ErrorLogFilters {
  status?: ErrorLogStatus;
  source?: ErrorLogSource;
  level?: 'ERROR' | 'FATAL';
  /** Tenant name contains. */
  tenant?: string;
  /** Method or route contains. */
  route?: string;
  search?: string;
  fingerprint?: string;
  /** ISO instants - the user's local day boundaries. */
  createdFrom?: string;
  createdTo?: string;
  sortBy?: 'createdAt' | 'occurrences' | 'source' | 'level' | 'statusCode';
  sortOrder?: 'asc' | 'desc';
  page: number;
  pageSize?: number;
}

export const ERROR_LOGS_PAGE_SIZE = 50;

export const listErrorLogs = async (
  filters: ErrorLogFilters,
): Promise<{ items: ErrorLog[]; meta: CollectionMeta }> => {
  const envelope = await apiClient.getCollection<ErrorLog>('/admin/error-logs', {
    query: { pageSize: ERROR_LOGS_PAGE_SIZE, ...filters },
  });
  return { items: envelope.data, meta: envelope.meta };
};

export type ErrorLogCounts = Record<'all' | ErrorLogStatus, number>;

/** Per-status counts for the tabs, under the same filters (status, paging and sort ignored). */
export function useErrorLogCounts(filters: ErrorLogFilters) {
  const { status: _s, page: _p, pageSize: _ps, sortBy: _b, sortOrder: _o, ...rest } = filters;
  return useQuery<ErrorLogCounts, ApiClientError>({
    queryKey: [...ERROR_LOGS_KEY, 'counts', rest],
    queryFn: () => apiClient.get<ErrorLogCounts>('/admin/error-logs/counts', { query: rest }),
    placeholderData: keepPreviousData,
  });
}
export const getErrorLog = (id: string) => apiClient.get<ErrorLogDetail>(`/admin/error-logs/${id}`);
export const updateErrorLogStatus = (id: string, input: UpdateErrorLogStatusInput) =>
  apiClient.patch<ErrorLogBulkResult>(`/admin/error-logs/${id}`, input);
export const archiveErrorLogs = (input: ArchiveErrorLogsInput) =>
  apiClient.post<ErrorLogBulkResult>('/admin/error-logs/archive', input);
export const purgeErrorLogs = (input: PurgeErrorLogsInput) =>
  apiClient.post<ErrorLogBulkResult>('/admin/error-logs/purge', input);

export function useErrorLogs(filters: ErrorLogFilters) {
  return useQuery<{ items: ErrorLog[]; meta: CollectionMeta }, ApiClientError>({
    queryKey: [...ERROR_LOGS_KEY, 'list', filters],
    queryFn: () => listErrorLogs(filters),
    placeholderData: keepPreviousData,
  });
}

export function useErrorLog(id: string | null) {
  return useQuery<ErrorLogDetail, ApiClientError>({
    queryKey: [...ERROR_LOGS_KEY, 'detail', id],
    queryFn: () => getErrorLog(id!),
    enabled: id !== null,
  });
}

export function useUpdateErrorLogStatus() {
  const qc = useQueryClient();
  return useMutation<
    ErrorLogBulkResult,
    ApiClientError,
    { id: string; input: UpdateErrorLogStatusInput }
  >({
    mutationFn: ({ id, input }) => updateErrorLogStatus(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ERROR_LOGS_KEY }),
  });
}

export function useArchiveErrorLogs() {
  const qc = useQueryClient();
  return useMutation<ErrorLogBulkResult, ApiClientError, ArchiveErrorLogsInput>({
    mutationFn: archiveErrorLogs,
    onSuccess: () => qc.invalidateQueries({ queryKey: ERROR_LOGS_KEY }),
  });
}

export function usePurgeErrorLogs() {
  const qc = useQueryClient();
  return useMutation<ErrorLogBulkResult, ApiClientError, PurgeErrorLogsInput>({
    mutationFn: purgeErrorLogs,
    onSuccess: () => qc.invalidateQueries({ queryKey: ERROR_LOGS_KEY }),
  });
}
